import { Dayjs } from 'dayjs'

import { add } from './addAndSubtract'
import { DATE_TEMPLATES } from './dateTemplates'
import { format } from './format'
import { getToday } from './getToday'
import { isSameOrBefore } from './isSameOrBefore'

type DateInterval = {
    startTime: string
    endTime: string
}

type DateIntervalsArgs = {
    end: Dayjs
    start?: Dayjs
    intervalLength?: number
}

/**
 * Calculates the date intervals between two dates inclusively based on a specified interval length.
 * The `endTime` in each interval is exclusive.
 *
 * @param {Dayjs} end - The end date.
 * @param {Dayjs} start - (Optional) The start date. Defaults to today.
 * @param {number} intervalLength - (Optional) The interval length in days. Defaults to 7.
 * @return - An array of date intervals, each with a start and end time in 'YYYY-MM-DD' format.
 */
export const getDateIntervals = ({ start, end, intervalLength = 7 }: DateIntervalsArgs) => {
    const dateIntervals: DateInterval[] = []

    let startTime = start ?? getToday()

    while (isSameOrBefore(startTime, end)) {
        const intervalEndDate = add(startTime, intervalLength, 'days')
        const endTime = isSameOrBefore(intervalEndDate, end) ? intervalEndDate : add(end, 1, 'day')

        dateIntervals.push({
            startTime: format(startTime, DATE_TEMPLATES.iso),
            endTime: format(endTime, DATE_TEMPLATES.iso),
        })

        startTime = intervalEndDate
    }

    return dateIntervals
}
